// The micro-animation EFFECT LIBRARY — reusable "this model is active / being
// worked" cues: smoke drifting from a chimney mouth, a fire mouth pulsing, a
// wheel spinning, a hung thing swaying. Like `programs.ts` (which generates the
// actor keyframes for interactions), each helper here is a pure generator: it
// returns plain {@link Part}s and/or keyframed {@link Track}s that the author
// spreads into a variant's parts and into an {@link Animation}'s tracks — so
// every building's chimney smokes with the same curve without re-authoring it
// per model. The convention the standard batch uses: one looping animation with
// id "active" on the `complete` variant, composed from these effects.
//
// All generators default to one shared loop length (3.2 s), so several effects
// dropped into the same animation cycle seamlessly together.

import * as THREE from "three";
import type { Keyframe, Part, Track, Vec3 } from "./types";

/** The shared default loop length (seconds) every effect generator uses, so
 *  effects composed into one animation wrap together. */
export const EFFECT_LOOP = 3.2;

/** Parts plus the tracks that drive them — one composable effect. Spread
 *  `parts` into the variant(s) the effect plays on and `tracks` into the
 *  animation: `tracks: [...plume.tracks, firePulse(...)]`. */
export interface Effect {
  /** Parts to add to the variant(s) the animation is restricted to. */
  parts: Part[];
  /** Tracks to add to the animation's `tracks`. */
  tracks: Track[];
}

function smoothstep(t: number): number {
  const c = t < 0 ? 0 : t > 1 ? 1 : t;
  return c * c * (3 - 2 * c);
}

export interface SmokePlumeOptions {
  /** Part-name prefix (default "smoke") — override when a model has two plumes. */
  id?: string;
  /** How many puffs ride the column at once (default 3). */
  puffs?: number;
  /** How far a puff rises over one cycle, in world units (default 0.45). */
  rise?: number;
  /** Sideways [x, z] wind drift over the full rise (default a light north-east). */
  drift?: readonly [number, number];
  /** Puff radius in world units (default 0.055). */
  radius?: number;
  /** Puff colour (default a light smoke grey). */
  color?: string;
  /** Loop length in seconds (default {@link EFFECT_LOOP}). */
  duration?: number;
}

/**
 * A looping smoke plume rising from a chimney `mouth` (the model-local point the
 * smoke appears at). Emits `puffs` icosahedron puffs staggered along the column;
 * each one grows out of the mouth, expands as it rises and drifts, and fades
 * (shrinks) away below the top — so the loop's wrap-around happens at invisible
 * scale. At rest (animation not playing) the puffs stand as a static column, the
 * same read the classic models' static smoke blocks had.
 */
export function smokePlume(mouth: Vec3, opts: SmokePlumeOptions = {}): Effect {
  const {
    id = "smoke",
    puffs = 3,
    rise = 0.45,
    drift = [0.06, -0.04],
    radius = 0.055,
    color = "#c9c9c9",
    duration = EFFECT_LOOP,
  } = opts;
  // A puff's life is parameterised by u in [0, 1): position climbs the column,
  // size grows in over the first fifth and fades out over [0.75, 0.95].
  const posAt = (u: number): Vec3 => [mouth[0] + drift[0] * u, mouth[1] + rise * u, mouth[2] + drift[1] * u];
  const sizeAt = (u: number): number =>
    Math.max(0.02, (0.5 + 0.7 * u) * smoothstep(u / 0.2) * (1 - smoothstep((u - 0.75) / 0.2)));

  const parts: Part[] = [];
  const tracks: Track[] = [];
  const steps = 12;
  for (let i = 0; i < puffs; i++) {
    const name = `${id}-${i}`;
    const phase = i / puffs;
    const rest = posAt(phase);
    parts.push({ geometry: new THREE.IcosahedronGeometry(radius, 0), color, position: rest, name });
    const position: Keyframe<Vec3>[] = [];
    const scale: Keyframe<number | Vec3>[] = [];
    for (let k = 0; k <= steps; k++) {
      const t = (duration * k) / steps;
      const u = (phase + k / steps) % 1;
      const p = posAt(u);
      position.push({ t, value: [p[0] - rest[0], p[1] - rest[1], p[2] - rest[2]] });
      scale.push({ t, value: sizeAt(u) });
    }
    tracks.push({ target: name, pivot: rest, position, scale });
  }
  return { parts, tracks };
}

export interface FirePulseOptions {
  /** Total keyframed length in seconds (default {@link EFFECT_LOOP}). */
  duration?: number;
  /** How many swell-and-settle beats fill the duration (default 4). */
  cycles?: number;
  /** Peak scale at the top of a beat (default 1.3; per-axis Vec3 allowed). */
  amount?: number | Vec3;
}

/**
 * A glowing-fire pulse: scales the named part (an ember block, a fire mouth)
 * up and back about `center` in sine beats — the flat-colour stand-in for a
 * flickering glow. Name the fire part and give its centre as the pivot.
 */
export function firePulse(target: string, center: Vec3, opts: FirePulseOptions = {}): Track {
  const { duration = EFFECT_LOOP, cycles = 4, amount = 1.3 } = opts;
  const scale: Keyframe<number | Vec3>[] = [{ t: 0, value: 1 }];
  const period = duration / cycles;
  for (let c = 0; c < cycles; c++) {
    scale.push({ t: c * period + period / 2, value: amount, ease: "sine" });
    scale.push({ t: (c + 1) * period, value: 1, ease: "sine" });
  }
  return { target, pivot: center, scale };
}

export interface SpinOptions {
  /** Rotation axis in the model frame (default "z" — an upright wheel). */
  axis?: "x" | "y" | "z";
  /** Loop length in seconds (default {@link EFFECT_LOOP}). */
  duration?: number;
  /** Full revolutions per loop; negative reverses direction (default 1). */
  turns?: number;
}

/** A constant-rate spin of the named part about `pivot` — a saw blade, a
 *  grindstone, a winch axle. One full turn per loop unless `turns` says more. */
export function spin(target: string, pivot: Vec3, opts: SpinOptions = {}): Track {
  const { axis = "z", duration = EFFECT_LOOP, turns = 1 } = opts;
  const at = (a: number): Vec3 => (axis === "x" ? [a, 0, 0] : axis === "y" ? [0, a, 0] : [0, 0, a]);
  return {
    target,
    pivot,
    rotation: [
      { t: 0, value: at(0) },
      { t: duration, value: at(-Math.PI * 2 * turns) },
    ],
  };
}

export interface SwayOptions {
  /** Rotation axis in the model frame (default "z"). */
  axis?: "x" | "y" | "z";
  /** Peak deflection in radians (default 0.05). */
  amount?: number;
  /** Loop length in seconds (default {@link EFFECT_LOOP}). */
  duration?: number;
}

/** A gentle pendulum sway of the named part about `pivot` (put the pivot where
 *  it hangs from) — a drying net, a hung sign, a lantern. */
export function sway(target: string, pivot: Vec3, opts: SwayOptions = {}): Track {
  const { axis = "z", duration = EFFECT_LOOP, amount = 0.05 } = opts;
  const at = (a: number): Vec3 => (axis === "x" ? [a, 0, 0] : axis === "y" ? [0, a, 0] : [0, 0, a]);
  return {
    target,
    pivot,
    rotation: [
      { t: 0, value: at(0) },
      { t: duration * 0.25, value: at(amount), ease: "sine" },
      { t: duration * 0.5, value: at(0), ease: "sine" },
      { t: duration * 0.75, value: at(-amount), ease: "sine" },
      { t: duration, value: at(0), ease: "sine" },
    ],
  };
}
